/** Admin page headers & section titles — English source for Translator Master + auto UI i18n */

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 100) || 'untitled';
}

/** Exact English → translation key (admin.<key> in API map). */
export const HEADER_EXACT_KEYS: Record<string, string> = {
  // —— Eyebrows ——
  Administration: 'admin.eyebrow.administration',
  Configuration: 'admin.eyebrow.configuration',
  Localization: 'admin.eyebrow.localization',
  'Partner Onboarding': 'admin.eyebrow.partner_onboarding',
  Grocery: 'admin.eyebrow.grocery',
  'Food Delivery': 'admin.eyebrow.food_delivery',
  Transport: 'admin.nav.transport',
  Transportation: 'admin.eyebrow.transportation',
  Onboarding: 'admin.nav.onboarding',
  'Customer App': 'admin.nav.customer_app',
  Finance: 'admin.eyebrow.finance',
  'Tenant Master': 'admin.nav.tenant_master',
  'Admin & Staff': 'admin.eyebrow.admin_staff',
  'Metadata Engine': 'admin.eyebrow.metadata_engine',
  'Dashboard Engine': 'admin.eyebrow.dashboard_engine',
  Platform: 'admin.eyebrow.platform',

  // —— Page titles ——
  'Users & Admins': 'admin.page.users',
  'Master Data': 'admin.page.master_data',
  'Translator Master': 'admin.translator.title',
  'Tenant Services': 'admin.page.tenant_services',
  'Driver Registration': 'admin.page.driver_registration',
  'Delivery Partner Onboarding': 'admin.page.delivery_partner_onboarding',
  'Merchant Onboarding': 'admin.page.merchant_onboarding',
  'Service Provider': 'admin.page.service_provider',
  'Grocery Orders': 'admin.page.grocery_orders',
  Stores: 'admin.page.stores',
  'Products & Inventory': 'admin.page.products_inventory',
  'Add Grocery Product': 'admin.page.add_grocery_product',
  'Edit Grocery Product': 'admin.page.edit_grocery_product',
  'Food Orders': 'admin.page.food_orders',
  Restaurants: 'admin.page.restaurants',
  'Food Catalog': 'admin.page.food_catalog',
  'Food Add': 'admin.page.food_add',
  'Edit Food Item': 'admin.page.edit_food_item',
  'Addon Groups': 'admin.page.addon_groups',
  'Scheduled Food Orders': 'admin.page.scheduled_food_orders',
  'Food Order History': 'admin.page.food_order_history',
  'Grocery Order History': 'admin.page.grocery_order_history',
  'Live Grocery Orders': 'admin.page.live_grocery_orders',
  'Live Food Orders': 'admin.page.live_food_orders',
  'Offers & Coupons': 'admin.page.offers_coupons',
  'Driver Management': 'admin.page.driver_management',
  'Driver Performance': 'admin.page.driver_performance',
  'Zone Pricing': 'admin.page.zone_pricing',
  'Rides Dashboard': 'admin.page.rides_dashboard',
  'Rental Management': 'admin.page.rental_management',
  'Outstation Management': 'admin.page.outstation_management',
  'Rental Packages': 'admin.page.rental_packages',
  'Vehicle Types': 'admin.page.vehicle_types',
  'Transport Sub-Services': 'admin.page.transport_sub_services',
  'Sub-Services moved to Transport Hub': 'admin.page.sub_services_moved',
  'Rides Operations': 'admin.page.rides_operations',
  'My Profile': 'admin.page.my_profile',
  'Document Review': 'admin.page.documents',
  'Zone Management': 'admin.page.zones',
  'Promos & Coupons': 'admin.page.promos',
  'Home Banners': 'admin.page.home_banners',
  'Commission Settlements': 'admin.page.settlements',
  'Onboarding Hub': 'admin.page.onboarding_hub',
  Helpdesk: 'admin.page.helpdesk',
  Partners: 'admin.page.partners',
  'Partner Subscription Plans': 'admin.page.partner_subscription_plans',
  'Subscription plans': 'admin.page.subscription_plans',
  Tenants: 'admin.page.tenants',
  'Full Function Reference': 'admin.page.function_reference',
  'Master Builder': 'admin.page.master_builder',
  'Dashboard Builder': 'admin.page.dashboard_builder',
  Dashboard: 'admin.page.dashboard',
  Delivery: 'admin.nav.delivery',
  'Locale & Currency': 'admin.page.locale',
  'Site Settings': 'admin.page.settings',
  Notifications: 'admin.page.notifications',
  'Roles & Privileges': 'admin.page.roles_privileges',
  'Control which admin screens each role can open — view or full manage access per page.': 'admin.page.roles_privileges_subtitle',
  'Payment Gateways': 'admin.page.payment_gateways',
  'SMS Gateways': 'admin.page.sms_gateways',
  'Audit Logs': 'admin.page.audit_logs',
  Reports: 'admin.page.reports',
  Settlements: 'admin.page.settlements_short',
  'Dispatch Center': 'admin.page.dispatcher',
  'SOS Command': 'admin.page.sos',

  // —— Subtitles ——
  'Manage tenant users, zone admins, partners, and staff. Assign operating zones to city admins for delegated zone management.': 'admin.page.users_subtitle',
  'Reference data for geography, pricing, operations, and payments — pick a type, add records.': 'admin.page.master_data_subtitle',
  'Strings grouped by Admin Panel, Customer App, Partner App, and Vendor App. Add a language once — English converts automatically.': 'admin.translator.subtitle',
  'Sub-services within enabled platform modules — taxi, delivery, etc.': 'admin.page.tenant_services_subtitle',
  'Complete the form and upload required documents. Your account activates after verification.': 'admin.page.onboarding_docs_subtitle',
  'Complete the form to onboard. Document verification is not required.': 'admin.page.onboarding_simple_subtitle',
  'Picking, packing, delivery workflow with stock-backed ordering.': 'admin.page.grocery_orders_subtitle',
  'Full store master — identity, location, compliance, delivery settings, timing, and revenue.': 'admin.page.stores_subtitle',
  '4-tab Grocery Add wizard — masters drive category, brand, variant, tax, and images.': 'admin.page.products_inventory_subtitle',
  '6-tab wizard — all 12 grocery masters drive category, supply, availability, and images.': 'admin.page.grocery_wizard_subtitle',
  'Accept orders, track kitchen-to-delivery workflow, and collect ratings.': 'admin.page.food_orders_subtitle',
  'Restaurant master — basic, business, compliance, timing, approval, commission or subscription revenue.': 'admin.page.restaurants_subtitle',
  '6-tab Food Add flow — variants, addons, tax, availability. No inventory or discounts.': 'admin.page.food_catalog_subtitle',
  '7-tab professional flow — only food-catalog masters (no inventory, recipe, or discount)': 'admin.page.food_add_subtitle',
  'Per-restaurant addon groups used in the Food Add wizard and customer customization.': 'admin.page.addon_groups_subtitle',
  'Upcoming orders scheduled for later delivery': 'admin.page.scheduled_food_orders_subtitle',
  'Delivered, cancelled, and refunded food orders': 'admin.page.food_order_history_subtitle',
  'Delivered, cancelled, and past restaurant orders': 'admin.page.food_order_history_vendor_subtitle',
  'Upcoming scheduled restaurant orders': 'admin.page.scheduled_food_vendor_subtitle',
  'Delivered, cancelled, and past store orders': 'admin.page.grocery_order_history_subtitle',
  'Accept store orders, pack items, mark ready, and track delivery': 'admin.page.live_grocery_orders_subtitle',
  'Accept incoming restaurant orders, mark ready, and track delivery': 'admin.page.live_food_orders_subtitle',
  'Restaurant promo codes for your food store': 'admin.page.offers_coupons_subtitle',
  'View, approve, and manage transport drivers — online status, trips, and documents.': 'admin.page.driver_management_subtitle',
  'Leaderboard rankings and incentive campaigns': 'admin.page.driver_performance_subtitle',
  'Configure RIDE NOW and RIDE LATER pricing per zone and vehicle type.': 'admin.page.zone_pricing_subtitle',
  'Monitor all rides with live status, filter by date range, status, and trip type.': 'admin.page.rides_dashboard_subtitle',
  'Select a zone to manage rental packages — zone-first flow like Caby rental master.': 'admin.page.rental_management_subtitle',
  'Manage hour/km rental packages for this zone. Same flow as Caby rental master.': 'admin.page.rental_zone_subtitle',
  'Select a zone to configure outstation KM slabs and per-vehicle pricing.': 'admin.page.outstation_management_subtitle',
  'KM slab + trip type packages — configure all vehicle fares in one add, like rental management.': 'admin.page.outstation_zone_subtitle',
  'Hour and kilometer rental package master — define packages per vehicle type.': 'admin.page.rental_packages_subtitle',
  'Manage vehicle categories — capacity, service type, and availability.': 'admin.page.vehicle_types_subtitle',
  'Dynamic bookable services from Transport Hub — zone pricing, not Ride Category masters': 'admin.page.transport_sub_services_subtitle',
  'Ride Category wizard is replaced by dynamic sub-service flow': 'admin.page.sub_services_moved_subtitle',
  'Book rides, advance workflow stages, assign drivers, and rate completed trips.': 'admin.page.rides_operations_subtitle',
  'Documents, vehicle, and bank details': 'admin.page.my_profile_subtitle',
  'Review uploaded partner documents and approve onboarding — configure document groups when needed.': 'admin.page.documents_subtitle',
  'View operating zones assigned to you. Zone creation and pricing changes are managed by tenant admin.': 'admin.page.zones_city_subtitle',
  'Zone-wise pricing for all services — Ride Now, Ride Later, Surge & per-vehicle transport rates': 'admin.page.zones_subtitle',
  'Discount codes for Food, Grocery, and Transport — synced to mobile checkout.': 'admin.page.promos_subtitle',
  'Carousel banners on Food, Grocery, and Transport tabs in the mobile app.': 'admin.page.home_banners_subtitle',
  'Partner commission settlements for transport, food delivery, and grocery delivery.': 'admin.page.settlements_subtitle',
  'Preview partner registration flows. Production partners onboard via the mobile app.': 'admin.page.onboarding_hub_subtitle',
  'Manage customer support tickets': 'admin.page.helpdesk_subtitle',
  'Manage drivers, merchants, and service providers — search, filter, and take action from one clean list.': 'admin.page.partners_subtitle',
  'Tenant-scoped prepaid plans for drivers and delivery partners — separate from platform tenant SaaS billing.': 'admin.page.partner_subscription_plans_subtitle',
  'SaaS tiers for tenants — pricing, limits, default modules, and plan highlights.': 'admin.page.subscription_plans_subtitle',
  'Create, configure, suspend, and manage SaaS clients with full lifecycle control.': 'admin.page.tenants_subtitle',
  'Complete capability breakdown — roles, permissions, and hub tab actions.': 'admin.page.function_reference_subtitle',
  'Create and edit master groups and types — no code deploy required.': 'admin.page.master_builder_subtitle',
  'Configure drag-and-drop KPI dashboards per role — driver, merchant, provider, admin, and more.': 'admin.page.dashboard_builder_subtitle',

  // —— Dashboard sections ——
  'Platform overview': 'admin.section.platform_overview',
  'Customers, revenue, zones, and service volumes': 'admin.section.platform_overview_sub',
  'Action items': 'admin.section.action_items',
  'Operations that need your attention': 'admin.section.action_items_sub',
  'Role-specific metrics and activity': 'admin.section.role_metrics_sub',
  '30-day service trends': 'admin.section.service_trends',
  'Transport and delivery volume and revenue': 'admin.section.service_trends_sub',
  'Recent activity': 'admin.section.recent_activity',
  'Latest transport and delivery jobs': 'admin.section.recent_activity_sub',
  'Quick access': 'admin.page.dashboard_quick_links',
  'Jump to key admin areas': 'admin.section.quick_access_sub',
  'Analytics command center': 'admin.section.analytics_command',
  'Transport and delivery revenue, volume, and performance': 'admin.section.analytics_command_sub',
  'Revenue trend (30 days)': 'admin.section.revenue_trend',
  'Stacked revenue — transport, food, and grocery': 'admin.section.revenue_trend_sub',
  'Revenue mix': 'admin.section.revenue_mix',
  'Share by service': 'admin.section.revenue_mix_sub',
  '24-hour live pulse': 'admin.section.live_pulse',
  'Hourly job volume — transport and delivery': 'admin.section.live_pulse_sub',
  'Service volume vs completed': 'admin.section.volume_vs_completed',
  'Volume and completion by service': 'admin.section.volume_vs_completed_sub',
  'Completed vs cancelled': 'admin.section.completed_vs_cancelled',
  'Per-service outcome comparison': 'admin.section.completed_vs_cancelled_sub',
  'User growth (30 days)': 'admin.section.user_growth',
  'New customer registrations': 'admin.section.user_growth_sub',
  'Period intelligence': 'admin.section.period_intelligence',
  'Completed vs cancelled trends and payment channel breakdown': 'admin.section.period_intelligence_sub',
  'Vendor Hub': 'admin.section.vendor_hub',
  'All store operations — food, grocery, menu, inventory, and payouts': 'admin.section.vendor_hub_sub',

  // —— Data table / panel headers ——
  'All Rides': 'admin.table.all_rides',
  'Corporate Clients': 'admin.table.corporate_clients',
  'Driver leaderboard': 'admin.table.driver_leaderboard',
  'Drivers Review List': 'admin.table.drivers_review_list',
  'Earnings Detail': 'admin.table.earnings_detail',
  'Food Items': 'admin.table.food_items',
  'Incentive campaigns': 'admin.table.incentive_campaigns',
  'Order Queue': 'admin.table.order_queue',
  'Product Catalog': 'admin.table.product_catalog',
  'Restaurant Addon Configuration': 'admin.table.restaurant_addon_config',
  'Restaurant Catalog': 'admin.table.restaurant_catalog',
  'Service Catalog': 'admin.table.service_catalog',
  'Store Catalog': 'admin.table.store_catalog',
  'Sub-Service Catalog': 'admin.table.sub_service_catalog',
  'Sub-services': 'admin.table.sub_services',
  'Your promos': 'admin.table.your_promos',
  'Scheduled Bookings': 'admin.table.scheduled_bookings',
  'Consultation Slots': 'admin.table.consultation_slots',
  'Venues & Bookings': 'admin.table.venues_bookings',
  'Live Tracking': 'admin.table.live_tracking',
  'All review list of drivers': 'admin.table.drivers_review_subtitle',
  'Discount codes applied at food checkout for your restaurant': 'admin.table.your_promos_subtitle',
  'End-to-end food delivery operations': 'admin.table.food_ops_subtitle',
  'Full CRUD with search, filter, and CSV export': 'admin.table.crud_export_subtitle',
  'Full product records with variants, addons, and metadata from Food Add Wizard': 'admin.table.food_items_subtitle',
  'Groups like Toppings, Sides, Beverages — each with priced addons.': 'admin.table.addon_groups_subtitle',
  'Operational control with live status transitions': 'admin.table.rides_ops_subtitle',
  'ride(s) matching filters': 'admin.table.rides_matching',
  in: 'admin.table.in',

  // —— Hub labels ——
  Taxi: 'admin.hub.taxi',
  Food: 'admin.hub.food',
  'Rides, drivers, bidding, zones & earnings': 'admin.hub.taxi_subtitle',
  'Restaurants, menus, live orders & partners': 'admin.hub.food_subtitle',
  'Stores, products, slots & deliveries': 'admin.hub.grocery_subtitle',
  'Taxi rides, drivers, zones & masters': 'admin.hub.transport_subtitle',
  'Food and Grocery services': 'admin.hub.delivery_subtitle',
  'Custom delivery sub-service': 'admin.hub.custom_delivery',
  'sub-service(s) · open to manage operations': 'admin.header.sub_services_open',
  'sub-service(s) · add custom Food/Grocery flows': 'admin.header.sub_services_add',

  // —— Rental / outstation panel headers ——
  'Bike Rental': 'admin.page.bike_rental',
  'Car Rental': 'admin.page.car_rental',
  'Rental Master': 'admin.page.rental_master',
  'Outstation Pricing Master': 'admin.page.outstation_pricing_master',
  'Zone-wise hour/km packages — prices used at booking when zone + package match.': 'admin.page.rental_packages_panel_subtitle',
  'Add packages with per-vehicle pricing. Status toggle and delete guards match Caby behavior.': 'admin.page.rental_packages_caby_subtitle',
  'Use Add Package to create hour/km bundles. Edit or Delete existing packages below.': 'admin.page.rental_packages_use_add_subtitle',
  'Select a zone to manage hour/km rental packages — same flow as Caby rental master.': 'admin.page.rental_hub_picker_subtitle',
  'Select a zone — configure pricing per vehicle and trip type (One Way / Round Trip).': 'admin.page.outstation_picker_subtitle',
  'KM slab + trip type packages with per-vehicle pricing — add all vehicles in one step, like rental management.': 'admin.page.outstation_panel_add_subtitle',
  'KM slab + trip type packages with per-vehicle pricing — same layout as rental management.': 'admin.page.outstation_panel_default_subtitle',
};

/** Flat keys for BASE_STRINGS.admin (without admin. prefix duplication). */
export function buildAdminHeaderBaseStrings(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [english, fullKey] of Object.entries(HEADER_EXACT_KEYS)) {
    const key = fullKey.startsWith('admin.') ? fullKey.slice('admin.'.length) : fullKey;
    out[key] = english;
  }
  return out;
}

export function resolveHeaderI18nKey(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return 'admin.header.empty';
  if (HEADER_EXACT_KEYS[trimmed]) return HEADER_EXACT_KEYS[trimmed];
  // Dynamic titles e.g. "Rental Packages — Zone A"
  const rentalMatch = trimmed.match(/^Rental Packages — (.+)$/);
  if (rentalMatch) return 'admin.page.rental_packages_zone';
  const outstationMatch = trimmed.match(/^Outstation Pricing — (.+)$/);
  if (outstationMatch) return 'admin.page.outstation_pricing_zone';
  return `admin.header.${slugify(trimmed)}`;
}
